import React from 'react'
import { Button, Heading, Text, LoadingSpinner } from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { useAuthContext } from '../../contexts/AuthContext'
import { supportCenterSpecialtiesService } from '../../services/supportCenterSpecialties'
import { supportCenterAssistancesService } from '../../services/supportCenterAssistances'
import type {
  SupportCenterSpecialization,
  SupportCenterSpecializationInput,
} from '../../types/supportCenterSpecialties'
import type { SupportCenterAssistance } from '../../types/supportCenterAssistances'
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline'
import {
  MODULE_NAME,
  EMPTY_SPEC_INPUT,
  formatDate,
  getErrorMessage,
} from '../../components/centralAmparo/parametrosHelpers'
import ParametrosSpecFormModal from './ParametrosSpecFormModal'
import ParametrosDeleteModal from './ParametrosDeleteModal'

const ParametrosEspecialidadesTab: React.FC = () => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')

  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [specializations, setSpecializations] = React.useState<SupportCenterSpecialization[]>([])
  const [assistancesCatalog, setAssistancesCatalog] = React.useState<SupportCenterAssistance[]>([])
  const [filter, setFilter] = React.useState('')

  const [formModal, setFormModal] = React.useState<SupportCenterSpecializationInput | null>(null)
  const [submittingForm, setSubmittingForm] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)

  const [deleteModal, setDeleteModal] = React.useState<{ id: string; label: string } | null>(null)
  const [deleteReason, setDeleteReason] = React.useState('')
  const [submittingDelete, setSubmittingDelete] = React.useState(false)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const [specs, assistances] = await Promise.all([
        supportCenterSpecialtiesService.listSpecializations(),
        supportCenterAssistancesService.list(),
      ])
      setSpecializations(specs)
      setAssistancesCatalog(assistances)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  const filteredSpecializations = filter.trim()
    ? specializations.filter((s) =>
        s.name.toLowerCase().includes(filter.trim().toLowerCase())
      )
    : specializations

  const openNewSpec = () => {
    setFormError(null)
    setFormModal({ ...EMPTY_SPEC_INPUT })
  }

  const openEditSpec = (item: SupportCenterSpecialization) => {
    setFormError(null)
    setFormModal({
      id: item.id,
      name: item.name,
      description: item.description ?? '',
      assistance_ids: item.assistances.map((a) => a.id),
    })
  }

  const closeFormModal = () => {
    if (submittingForm) return
    setFormModal(null)
    setFormError(null)
  }

  const handleFormSave = async () => {
    if (!formModal) return
    if (!formModal.name.trim()) {
      setFormError('O nome é obrigatório.')
      return
    }
    if (formModal.assistance_ids.length === 0) {
      setFormError('Vincule ao menos uma assistência.')
      return
    }
    setSubmittingForm(true)
    setFormError(null)
    try {
      const isEdit = Boolean(formModal.id)
      const savedId = await supportCenterSpecialtiesService.upsertSpecialization(
        formModal,
        user?.id
      )
      void (isEdit
        ? logUpdate(
            'support_center_specialty',
            formModal.id ?? null,
            `Atualizou especialidade: ${formModal.name.trim()}`,
            undefined,
            {
              name: formModal.name.trim(),
              assistance_ids: formModal.assistance_ids,
            }
          )
        : logCreate('support_center_specialty', savedId, `Criou especialidade: ${formModal.name.trim()}`, {
            name: formModal.name.trim(),
            assistance_ids: formModal.assistance_ids,
          }))
      closeFormModal()
      await loadData()
    } catch (error) {
      setFormError(getErrorMessage(error))
    } finally {
      setSubmittingForm(false)
    }
  }

  const closeDeleteModal = () => {
    if (submittingDelete) return
    setDeleteModal(null)
    setDeleteReason('')
  }

  const confirmDelete = async () => {
    if (!deleteModal || !user?.id || !deleteReason.trim()) return
    setSubmittingDelete(true)
    try {
      await supportCenterSpecialtiesService.softDeleteSpecialization(
        deleteModal.id,
        deleteReason.trim(),
        user.id
      )
      void logDelete('support_center_specialty', deleteModal.id, `Excluiu especialidade: ${deleteModal.label}`, {
        reason: deleteReason.trim(),
      })
      closeDeleteModal()
      await loadData()
    } catch (error) {
      setPageError(getErrorMessage(error))
      closeDeleteModal()
    } finally {
      setSubmittingDelete(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      {pageError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{pageError}</Text>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
              Especialidades
            </Heading>
            <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
              {filteredSpecializations.length}
              {filter.trim() && filteredSpecializations.length !== specializations.length
                ? ` de ${specializations.length}`
                : ''}{' '}
              {filteredSpecializations.length === 1 ? 'registro' : 'registros'}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtrar por nome..."
              className="w-48 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {canInsert && (
              <Button variant="solid" size="md" onClick={openNewSpec}>
                <PlusIcon className="h-4 w-4" />
                Nova Especialidade
              </Button>
            )}
          </div>
        </div>

        {filteredSpecializations.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Text size="sm" color="muted">
              {filter.trim()
                ? `Nenhum resultado para "${filter}".`
                : 'Nenhuma especialidade cadastrada.'}
            </Text>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Descrição
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Assistências
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Criado em
                  </th>
                  {(canUpdate || canDelete) && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Ações
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredSpecializations.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {item.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                      {item.description || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {item.assistances.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {item.assistances.map((assistance) => (
                            <span
                              key={assistance.id}
                              className="inline-flex px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                              title={assistance.name}
                            >
                              {assistance.code}
                            </span>
                          ))}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatDate(item.created_at)}
                    </td>
                    {(canUpdate || canDelete) && (
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {canUpdate && (
                            <button
                              type="button"
                              title="Editar"
                              onClick={() => openEditSpec(item)}
                              className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              title="Excluir"
                              onClick={() => {
                                setDeleteModal({ id: item.id, label: item.name })
                                setDeleteReason('')
                              }}
                              className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formModal && (
        <ParametrosSpecFormModal
          formModal={formModal}
          assistancesCatalog={assistancesCatalog}
          formError={formError}
          submitting={submittingForm}
          onFormChange={setFormModal}
          onClose={closeFormModal}
          onSave={() => void handleFormSave()}
        />
      )}

      {deleteModal && (
        <ParametrosDeleteModal
          label={deleteModal.label}
          deleteReason={deleteReason}
          submitting={submittingDelete}
          onReasonChange={setDeleteReason}
          onClose={closeDeleteModal}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </div>
  )
}

export default ParametrosEspecialidadesTab
